import {
  builtInToolName,
  itemIdOf,
  pinnedReferences,
  targetKey,
  type ArtifactSummary,
  type ItemTarget,
  type NamedItem,
  type ProcessCatalogue,
  type StepSummary,
  type ToolSummary,
  type WorkflowDefinition,
} from "../../../api/content/processDefinitions";
import type { ValueKind } from "../../../api/content/authoring/selectorDecoders";
import type { ListedItem, ProcessItem, RoleBindings } from "../../../api/content/processItemsApi";
import { isAbortError } from "../../../api/http/httpClient";
import { reportUiError, toUiError } from "../../../errors/uiError";

/**
 * Reads a Workflow and everything the view can open from it: its steps, sub-workflows,
 * the tools its tool steps run, the items its steps' selectors name (by ID, version or
 * role), and the Workflows that made those artifacts, wave by wave with the reads of one
 * wave in parallel. Only the Workflow itself must be readable; any other item that fails
 * stays out of the catalogue and is drawn as unresolved.
 */

export interface ProcessReader {
  readItem(reference: string): Promise<ProcessItem>;
  /** The latest version, kind and name of floating items; a missing one has no version. */
  findLatestItems(itemIds: ReadonlySet<string>): Promise<ReadonlyMap<string, ListedItem>>;
  /** The project's bindings (PG-5): the artifact that holds them, and role name → item ID. */
  findRoleBindings(): Promise<RoleBindings>;
  /** The `VersionRef`s of the project's Workflows. */
  listWorkflows(): Promise<readonly string[]>;
  /** The built-in tools by `builtin:<name>@<version>`; empty without a project. */
  readBuiltInTools(): Promise<ReadonlyMap<string, ToolSummary>>;
  /** The value kind each selector version renders, by `selectorVersionKey`; empty without a project. */
  readSelectorValueKinds(): Promise<ReadonlyMap<string, ValueKind | null>>;
  /** Items of each kind in the project's latest content version; empty without a project. */
  countItemsByKind(): Promise<ReadonlyMap<string, number>>;
  /** The readable version of each item version that has one; empty without a project. */
  readVersions(references: readonly string[]): Promise<ReadonlyMap<string, string>>;
}

export interface LoadedProcess {
  readonly workflow: WorkflowDefinition;
  readonly catalogue: ProcessCatalogue;
}

/** A guard against runaway item reads, the root and tools included; a seed Workflow needs a few dozen. */
export const MAXIMUM_PROCESS_READS = 200;

export async function loadProcess(reference: string, reader: ProcessReader): Promise<LoadedProcess> {
  const root = await reader.readItem(reference);
  if (root.kind !== "workflow") {
    throw new Error(`${reference} is not a Workflow`);
  }
  const catalogue = new CatalogueReader(reader, reference, root.workflow);
  await catalogue.read(followWorkflow(root.workflow), true);
  // Counts need only the steps read so far; tools wait for the producers' steps too.
  const counts = countsIfNeeded(catalogue.steps, reader);
  const declaredProducers = await findDeclaredProducers(catalogue, reader);
  const readsSelectors = [...catalogue.steps.values()].some((step) => step.selectorReads.length > 0);
  // The input cards show the items read by their readable version (F61, ADR-0033).
  const read = [...[...catalogue.artifacts.values()].map((artifact) => artifact.reference), ...[...catalogue.namedItems.values()].map((item) => item.reference)];
  const [tools, itemCounts, selectorValueKinds, versions, bindings] = await Promise.all([
    readTools(catalogue, reader),
    counts,
    readsSelectors ? orNothing(reader.readSelectorValueKinds()) : null,
    read.length === 0 ? null : orNothing(reader.readVersions(read)),
    catalogue.bindingsRead(),
  ]);
  return {
    workflow: root.workflow,
    catalogue: {
      workflows: catalogue.workflows,
      steps: catalogue.steps,
      tools,
      artifacts: catalogue.artifacts,
      namedItems: catalogue.namedItems,
      itemCounts,
      declaredProducers,
      selectorValueKinds: selectorValueKinds ?? new Map(),
      versions: versions ?? new Map(),
      bindings,
    },
  };
}

/** A process item to read, or an item a selector names, stored under its `targetKey`. */
type Wanted = { readonly kind: "process"; readonly reference: string } | { readonly kind: "target"; readonly key: string; readonly target: ItemTarget };

/** A read to make: `key` is set for a selector's item. */
interface Pending {
  readonly reference: string;
  readonly key: string | null;
}

class CatalogueReader {
  readonly workflows = new Map<string, WorkflowDefinition>();
  readonly steps = new Map<string, StepSummary>();
  readonly artifacts = new Map<string, ArtifactSummary>();
  readonly namedItems = new Map<string, NamedItem>();
  private readonly requested = new Set<string>();
  private roles: Promise<RoleBindings> | null = null;

  constructor(
    private readonly reader: ProcessReader,
    reference: string,
    workflow: WorkflowDefinition,
  ) {
    this.workflows.set(reference, workflow);
    this.requested.add(requestKey({ reference, key: null }));
  }

  /** Reads `wanted` and what it leads to; a step's selector items only with `followReads`. */
  async read(wanted: readonly Wanted[], followReads: boolean): Promise<void> {
    let wave = await this.resolve(wanted);
    while (wave.length > 0 && this.requested.size < MAXIMUM_PROCESS_READS) {
      const next: Wanted[] = [];
      for (const { pending, item } of await this.readItems(wave)) {
        if (item !== null) {
          next.push(...this.keep(pending, item, followReads));
        }
      }
      wave = await this.resolve(next);
    }
  }

  /** Reads each item not read before, as far as `MAXIMUM_PROCESS_READS` admits. */
  async readItems(wave: readonly Pending[]) {
    const fresh = [...new Map(wave.map((pending) => [requestKey(pending), pending])).entries()].filter(([key]) => !this.requested.has(key));
    const admitted = fresh.slice(0, Math.max(0, MAXIMUM_PROCESS_READS - this.requested.size));
    for (const [key] of admitted) {
      this.requested.add(key);
    }
    return readWave(admitted.map(([, pending]) => pending), this.reader);
  }

  /** The item a target names, as an ID: a role through the project's bindings. */
  async itemIdOf(target: ItemTarget): Promise<string | null> {
    switch (target.by) {
      case "item":
        return target.item;
      case "version":
        return itemIdOf(target.version);
      case "role":
        return (await this.roleBindings()).roles.get(target.role) ?? null;
    }
  }

  private keep(pending: Pending, item: ProcessItem, followReads: boolean): Wanted[] {
    if (pending.key !== null) {
      if (item.kind === "artifact") {
        this.artifacts.set(pending.key, item.artifact);
        return item.artifact.madeBy === null ? [] : [{ kind: "process", reference: item.artifact.madeBy.workflow }];
      }
      // A selector may name any item: a step, a Workflow or a tool is named like the rest.
      this.namedItems.set(pending.key, namedItemOf(item));
      return [];
    }
    if (item.kind === "workflow") {
      this.workflows.set(pending.reference, item.workflow);
      return followWorkflow(item.workflow);
    }
    if (item.kind === "step") {
      this.steps.set(pending.reference, item.step);
      return followReads ? followStep(item.step) : [];
    }
    return [];
  }

  /**
   * Floating targets become the latest version of the item. An artifact is read for who
   * made it and a label list for its labels; any other item is known from the listing.
   */
  private async resolve(wanted: readonly Wanted[]): Promise<Pending[]> {
    const pending: Pending[] = [];
    const floating = new Map<string, string>();
    for (const entry of wanted) {
      if (entry.kind === "process") {
        pending.push({ reference: entry.reference, key: null });
      } else if (entry.target.by === "version") {
        pending.push({ reference: entry.target.version, key: entry.key });
      } else {
        const itemId = await this.itemIdOf(entry.target);
        if (itemId !== null) {
          floating.set(entry.key, itemId);
        }
      }
    }
    if (floating.size === 0) {
      return pending;
    }
    const latest = await this.reader.findLatestItems(new Set(floating.values()));
    for (const [key, itemId] of floating) {
      const listed = latest.get(itemId);
      if (listed?.kind === "artifact" || listed?.kind === "label_list") {
        pending.push({ reference: listed.reference, key });
      } else if (listed !== undefined) {
        this.namedItems.set(key, { reference: listed.reference, name: listed.name, kind: listed.kind, labelCount: null });
      }
    }
    return pending;
  }

  private roleBindings(): Promise<RoleBindings> {
    this.roles ??= this.reader.findRoleBindings();
    return this.roles;
  }

  /** The bindings artifact, once a role was resolved through it; `null` when none was. */
  async bindingsRead(): Promise<string | null> {
    return this.roles === null ? null : (await this.roles).reference;
  }
}

/** Any item but an artifact, as a selector's source: its name and kind. */
function namedItemOf(item: Exclude<ProcessItem, { kind: "artifact" }>): NamedItem {
  switch (item.kind) {
    case "other":
      return item.item;
    case "step":
      return { reference: item.step.reference, name: item.step.name, kind: "step", labelCount: null };
    case "workflow":
      return { reference: item.workflow.reference, name: item.workflow.name, kind: "workflow", labelCount: null };
    case "tool":
      return { reference: item.tool.reference, name: item.tool.name, kind: "tool", labelCount: null };
  }
}

function requestKey(pending: Pending): string {
  return pending.key === null ? pending.reference : `${pending.reference}\n${pending.key}`;
}

function followWorkflow(workflow: WorkflowDefinition): Wanted[] {
  // A use's bound definitions are read too, so its abstract steps draw what fills them (ADR-0043 D2).
  return workflow.steps.flatMap((step) => pinnedReferences(step).map((reference): Wanted => ({ kind: "process", reference })));
}

function followStep(step: StepSummary): Wanted[] {
  return step.selectorReads.flatMap((read) => read.targets.map((target): Wanted => ({ kind: "target", key: targetKey(target), target })));
}

/**
 * For each artifact no Workflow run made (seeded, or edited by hand): the project's
 * Workflow with a step that proposes it, read like any other input so that it opens in
 * place. The first in the project's listing wins.
 */
async function findDeclaredProducers(catalogue: CatalogueReader, reader: ProcessReader): Promise<ReadonlyMap<string, string>> {
  const unmade = new Set([...catalogue.artifacts.values()].filter((artifact) => artifact.madeBy === null).map((artifact) => itemIdOf(artifact.reference)));
  if (unmade.size === 0) {
    return new Map();
  }
  const workflows = await reader.listWorkflows();
  await catalogue.read(
    workflows.map((reference) => ({ kind: "process", reference })),
    false,
  );
  const producers = new Map<string, string>();
  for (const reference of workflows) {
    for (const use of catalogue.workflows.get(reference)?.steps ?? []) {
      const proposes = use.kind === "step" ? (catalogue.steps.get(use.step)?.proposes ?? null) : null;
      const itemId = proposes === null ? null : await catalogue.itemIdOf(proposes);
      if (itemId !== null && unmade.has(itemId) && !producers.has(itemId)) {
        producers.set(itemId, reference);
      }
    }
  }
  const producerSteps = [...new Set(producers.values())].flatMap((reference) => catalogue.workflows.get(reference)?.steps ?? []);
  await catalogue.read(
    producerSteps.flatMap((use) => {
      const step = use.kind === "step" ? catalogue.steps.get(use.step) : undefined;
      return step === undefined ? [] : followStep(step);
    }),
    true,
  );
  return producers;
}

const isBuiltIn = (tool: string) => builtInToolName(tool) !== null;

/** The tool of every tool step: built-in ones from the step catalogue, tool items read. */
async function readTools(catalogue: CatalogueReader, reader: ProcessReader): Promise<ReadonlyMap<string, ToolSummary>> {
  const references = new Set([...catalogue.steps.values()].flatMap((step) => (step.tool === null ? [] : [step.tool])));
  if (references.size === 0) {
    return new Map();
  }
  const [builtIn, items] = await Promise.all([
    [...references].some(isBuiltIn) ? orNothing(reader.readBuiltInTools()) : Promise.resolve(null),
    catalogue.readItems([...references].filter((reference) => !isBuiltIn(reference)).map((reference) => ({ reference, key: null }))),
  ]);
  const tools = new Map<string, ToolSummary>();
  for (const reference of references) {
    const tool = isBuiltIn(reference) ? builtIn?.get(reference) : undefined;
    if (tool !== undefined) {
      tools.set(reference, tool);
    }
  }
  for (const { pending, item } of items) {
    if (item?.kind === "tool") {
      tools.set(pending.reference, item.tool);
    }
  }
  return tools;
}

/** The project's item counts, read only for a step whose selector reads a kind as a whole. */
async function countsIfNeeded(steps: ReadonlyMap<string, StepSummary>, reader: ProcessReader): Promise<ReadonlyMap<string, number> | null> {
  const needed = [...steps.values()].some((step) => step.selectorReads.some((read) => read.selector.kind === "competency_questions"));
  return needed ? orNothing(reader.countItemsByKind()) : null;
}

/** A read the view can do without: its failure is reported and leaves the fact unknown. */
async function orNothing<T>(read: Promise<T>): Promise<T | null> {
  try {
    return await read;
  } catch (caught) {
    if (isAbortError(caught)) {
      throw caught;
    }
    reportUiError(toUiError(caught));
    return null;
  }
}

async function readWave(wave: readonly Pending[], reader: ProcessReader) {
  return Promise.all(
    wave.map(async (pending) => {
      try {
        return { pending, item: await reader.readItem(pending.reference) };
      } catch (caught) {
        if (isAbortError(caught)) {
          throw caught;
        }
        reportUiError(toUiError(caught));
        return { pending, item: null };
      }
    }),
  );
}
